"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { authApi } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";

function LoginInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialError = searchParams.get("error");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then(({ data }) => {
      if (data) router.replace("/dashboard");
    });
  }, [router]);

  const errorMessages: Record<string, string> = {
    auth_failed: "Authentication failed. Please try again.",
    no_code: "No authorization code received.",
    access_denied: "Access was denied.",
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    const res = await authApi.sendOtp(email);
    if (res.error) {
      setError(res.error);
      setLoading(false);
    } else if (res.data?.success) {
      router.prefetch("/dashboard");
      setStep("otp");
      setMessage("Verification code sent to your email.");
      setLoading(false);
    } else {
      setError("Failed to send OTP.");
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const res = await authApi.verifyOtp(email, otp);
    if (res.error) {
      setError(res.error);
      setLoading(false);
    } else if (res.data?.success) {
      trackEvent("login", { method: "otp" });
      router.replace("/dashboard");
    } else {
      setError("Invalid verification code.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: "#0A0908" }}>
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none opacity-20" style={{ background: "radial-gradient(circle at center, rgba(201,168,76,0.15), transparent 70%)" }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] pointer-events-none opacity-10" style={{ background: "radial-gradient(circle at center, rgba(201,168,76,0.1), transparent 70%)" }} />

      <div className="w-full max-w-md px-8 py-12 relative z-10 mx-4 transition-all duration-500" 
           style={{ 
             background: "rgba(255,255,255,0.01)", 
             border: "1px solid rgba(201,168,76,0.15)",
             backdropFilter: "blur(12px)"
           }}>
        
        {/* Animated accent line */}
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)" }} />

        <div className="flex flex-col items-center mb-10 gap-5">
          <div className="w-16 h-16 flex items-center justify-center transition-transform duration-500 hover:scale-105 overflow-hidden" 
               style={{ border: "1px solid rgba(201,168,76,0.3)" }}>
            <img src="/squre-logo.png" alt="Aurelius & Co" className="w-full h-full object-contain p-2" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-light tracking-[0.15em] uppercase" style={{ color: "#F5F0E8", fontFamily: "var(--font-cormorant), serif" }}>
              Aurelius & Co
            </h1>
            <p className="text-[10px] tracking-[0.1em] mt-2 opacity-40 uppercase" style={{ color: "#F5F0E8", fontFamily: "var(--font-montserrat), sans-serif" }}>
              Enterprise Instagram Automation
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 text-[11px] tracking-wide text-center flex items-center justify-center gap-3 transition-all animate-in fade-in slide-in-from-top-2 duration-300" 
               style={{ 
                 background: "rgba(239,68,68,0.08)", 
                 border: "1px solid rgba(239,68,68,0.25)", 
                 color: "#fca5a5",
                 fontFamily: "var(--font-montserrat), sans-serif" 
               }}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{errorMessages[error as string] ?? error}</span>
          </div>
        )}

        {message && (
          <div className="mb-6 px-4 py-3 text-xs tracking-wide text-center" 
               style={{ 
                 background: "rgba(201,168,76,0.05)", 
                 border: "1px solid rgba(201,168,76,0.2)", 
                 color: "#C9A84C",
                 fontFamily: "var(--font-montserrat), sans-serif" 
               }}>
            {message}
          </div>
        )}

        <div className="flex flex-col gap-6">
          {step === "email" ? (
            <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest opacity-40 px-1" style={{ color: "#F5F0E8" }}>Work Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  required
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                  style={{ color: "#F5F0E8" }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 transition-all duration-300 relative overflow-hidden disabled:opacity-50"
                style={{ 
                  background: "#C9A84C", 
                  color: "#0A0908", 
                  fontFamily: "var(--font-montserrat), sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em"
                }}
              >
                {loading ? "SENDING..." : "SEND LOGIN CODE"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest opacity-40 px-1" style={{ color: "#F5F0E8" }}>Verification Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="000000"
                  required
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-center tracking-[1em] focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                  style={{ color: "#F5F0E8" }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 transition-all duration-300 relative overflow-hidden disabled:opacity-50"
                style={{ 
                  background: "#C9A84C", 
                  color: "#0A0908", 
                  fontFamily: "var(--font-montserrat), sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em"
                }}
              >
                {loading ? "VERIFYING..." : "VERIFY CODE"}
              </button>
              <button 
                type="button" 
                onClick={() => setStep("email")}
                className="text-[10px] uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
                style={{ color: "#F5F0E8" }}
              >
                Back to Email
              </button>
            </form>
          )}

          <div className="relative flex items-center gap-4 py-2">
            <div className="flex-1 h-px opacity-10" style={{ background: "#F5F0E8" }} />
            <span className="text-[10px] opacity-20 uppercase tracking-widest" style={{ color: "#F5F0E8" }}>or</span>
            <div className="flex-1 h-px opacity-10" style={{ background: "#F5F0E8" }} />
          </div>

          <a
            href={authApi.getGoogleAuthUrl()}
            className="flex items-center justify-center gap-3 w-full px-6 py-3 transition-all duration-300 group relative overflow-hidden border border-white/10 hover:border-[#C9A84C]/30"
            style={{ 
              background: "transparent", 
              color: "#F5F0E8", 
              fontFamily: "var(--font-montserrat), sans-serif",
              fontSize: "0.75rem",
              letterSpacing: "0.05em"
            }}
          >
            <GoogleIcon />
            CONTINUE WITH GOOGLE
          </a>

          <p className="text-[10px] text-center leading-relaxed opacity-30 uppercase tracking-[0.05em] mt-4" 
             style={{ color: "#F5F0E8", fontFamily: "var(--font-montserrat), sans-serif" }}>
            By continuing, you agree to our{" "}
            <a href="/privacy" className="hover:text-[#C9A84C] transition-colors underline underline-offset-4">Privacy Policy</a>
            {" "}and{" "}
            <a href="/terms" className="hover:text-[#C9A84C] transition-colors underline underline-offset-4">Terms of Service</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="w-8 h-8 rounded-full border-2 border-[#C9A84C] border-t-transparent animate-spin" />
      </div>
    }>
      <LoginInner />
    </Suspense>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
